import { useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Card, F0Dialog } from '@factorialco/f0-react'
import {
  AtsAssignVacancyToJobApplicationDocument,
  AtsAssignVacancyToJobApplicationMutation,
  AtsJobApplicationHeaderDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { VacancySelector } from 'modules/ats/components/VacancySelector'

type OfferLetterOption = 'create-offer-letter-from-template' | 'send-offer-letter-from-scratch'

interface Props {
  onClose: () => void
  onSelectOption: (option: OfferLetterOption) => void
  applicationId: string
  jobPostingId: string
  isHiringPlanEnabled: boolean
  hasVacancy: boolean
}

export const SendOfferLetterModal = ({
  onClose,
  onSelectOption,
  applicationId,
  jobPostingId,
  isHiringPlanEnabled,
  hasVacancy,
}: Props) => {
  const [selectedOption, setSelectedOption] = useState<OfferLetterOption | undefined>(undefined)
  const [selectedVacancyId, setSelectedVacancyId] = useState<string | undefined>(undefined)
  const [isAssigning, setIsAssigning] = useState(false)

  const [assignVacancy, { client }] = useMutation(AtsAssignVacancyToJobApplicationDocument, {
    extractorFn: (result: AtsAssignVacancyToJobApplicationMutation) =>
      result.atsMutations.assignApplicationAtsVacancy,
  })

  const handleNext = async () => {
    if (!selectedOption) return

    if (selectedVacancyId) {
      setIsAssigning(true)

      try {
        const result = await assignVacancy({
          variables: {
            applicationId,
            id: selectedVacancyId,
          },
        })

        const handled = handleResult(result, () => {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('ats.vacancy_selector.error_message'),
          })
        })

        if (!handled.success) return

        void client.refetchQueries({ include: [AtsJobApplicationHeaderDocument] })
      } finally {
        setIsAssigning(false)
      }
    }

    onSelectOption(selectedOption)
  }

  const showVacancySelector = isHiringPlanEnabled && !hasVacancy

  const offerTypeCards = (
    <>
      <Text.Normal size="200">{i18n.t('ats.send_offer_letter_modal.selector.label')}</Text.Normal>
      <Box gap="s16" flexDirection="row" paddingTop="s16">
        <F0Card
          title={i18n.t('ats.send_offer_letter_modal.selector.from_scratch_title')}
          description={i18n.t('ats.send_offer_letter_modal.selector.from_scratch_description')}
          selectable={isHiringPlanEnabled}
          selected={selectedOption === 'send-offer-letter-from-scratch'}
          onSelect={
            isHiringPlanEnabled
              ? () => setSelectedOption('send-offer-letter-from-scratch')
              : undefined
          }
          onClick={
            !isHiringPlanEnabled
              ? () => onSelectOption('send-offer-letter-from-scratch')
              : undefined
          }
        />
        <F0Card
          title={i18n.t('ats.send_offer_letter_modal.selector.from_template_title')}
          description={i18n.t('ats.send_offer_letter_modal.selector.from_template_description')}
          selectable={isHiringPlanEnabled}
          selected={selectedOption === 'create-offer-letter-from-template'}
          onSelect={
            isHiringPlanEnabled
              ? () => setSelectedOption('create-offer-letter-from-template')
              : undefined
          }
          onClick={
            !isHiringPlanEnabled
              ? () => onSelectOption('create-offer-letter-from-template')
              : undefined
          }
        />
      </Box>
    </>
  )

  if (!isHiringPlanEnabled) {
    return (
      <F0Dialog
        onClose={onClose}
        isOpen
        width="sm"
        title={i18n.t('ats.send_offer_letter_modal.title')}
      >
        {offerTypeCards}
      </F0Dialog>
    )
  }

  return (
    <F0Dialog
      onClose={onClose}
      isOpen
      width="sm"
      title={i18n.t('ats.send_offer_letter_modal.title')}
      primaryAction={{
        label: i18n.t('ats.vacancy_selector.next_button'),
        onClick: handleNext,
        disabled: !selectedOption || isAssigning,
      }}
      secondaryAction={{
        label: i18n.t('ats.vacancy_selector.cancel_button'),
        onClick: onClose,
      }}
    >
      {offerTypeCards}
      {showVacancySelector && (
        <Box paddingTop="s24">
          <Text.Normal size="200" weight="semibold">
            {i18n.t('ats.send_offer_letter_modal.link_candidate_question')}
          </Text.Normal>
          <Box paddingTop="s16">
            <VacancySelector
              jobPostingId={jobPostingId}
              value={selectedVacancyId}
              onChange={setSelectedVacancyId}
              applicationId={applicationId}
            />
          </Box>
        </Box>
      )}
    </F0Dialog>
  )
}
